import { Input, OnInit, OnDestroy, Component, ChangeDetectorRef, Optional, OnChanges, SimpleChanges } from '@angular/core';
import {
  AsyncValidatorFn,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { BaseFpxDataService, CriteriaQuery } from '@fpx/common/models';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { HttpRequest, LookUpFn } from '../models/http-provider.model';

@Component({
  template: ``,
})
export abstract class BaseFpxControlComponent
  implements OnInit, OnDestroy, ControlValueAccessor {
  /** @description Input Output variables */
  @Input() changePublisher!: Subject<{
    subject: string;
    payload: any;
  }>;
  @Input() formControlName!: string;
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() dependencyField: string[] = [];
  @Input() controlName: string = '';
  @Input() selectableList$: Observable<Array<any>> = of([]);
  @Input() controlTemplate: string;

  private _registeredDependencies: string[] = [];
  private _dropdownMode: 'BASIC' | 'SEARCHABLE' = 'BASIC';
  private readonly changeDetectorRef: ChangeDetectorRef;

  /** @description Template variables */
  selectableDataList$!: Observable<any>;
  formControl!: FormControl;
  loading: boolean = false;
  errorCode: string = '';
  private readonly input$ = new Subject<any>();

  inputChange$ = this.input$.asObservable();

  /** @description Private Variables */
  private readonly _syncValidators: ValidatorFn[] = [];
  private readonly _asyncValidators: AsyncValidatorFn[] = [];
  private readonly notifyContext: Map<string, any> = new Map<string, any>();
  private readonly _controlContainer: ControlContainer;
  private changePublisher$!: Subscription;
  private controlStatus$!: Subscription;
  private _dataService!: BaseFpxDataService<unknown>;

  constructor(controlContainer: ControlContainer, changeDetectorRef: ChangeDetectorRef) {
    this._controlContainer = controlContainer;
    this.changeDetectorRef = changeDetectorRef;
  }

  /** @description protected members */
  protected addSyncValidatorFn(syncValidatorFn: ValidatorFn) {
    this._syncValidators.push(syncValidatorFn);
  }

  protected addAsyncValidatorFn(asyncValidatorFn: AsyncValidatorFn) {
    this._asyncValidators.push(asyncValidatorFn);
  }

  protected setSelectableData(selectableDataList: LookUpFn<any>) {
    this.selectableDataList$ = selectableDataList().pipe((res) => res);
  }

  protected addDependencies(dependencyList: string[]) {
    this._registeredDependencies = dependencyList;
  }

  protected setDataSource(service: BaseFpxDataService<unknown>): void {
    this._dataService = service;
  }
  protected setDropdownMode(mode: 'BASIC' | 'SEARCHABLE'): void {
    this._dropdownMode = mode;
  }

  /** @description init lifecycle hook */
  ngOnInit(): void {
    if (this.controlName) {
      this.formControlName = this.controlName;
    }
    let formGroup = this._controlContainer.control as FormGroup;
    this.formControl = formGroup.controls[this.formControlName] as FormControl;
    this.doPreInit();
    this.formControl.addValidators(this._syncValidators);
    this.formControl.addAsyncValidators(this._asyncValidators);
    this.formControl.updateValueAndValidity();

    if (this.changePublisher) {
      this.changePublisher$ = this.changePublisher
        .asObservable()
        .subscribe((response) => {
          this.notifyContext.set(response.subject, response.payload);
          if (this.dependencyField.includes(response.subject)) {
            let dependentFieldValue: any = {};
            for (let index = 0; index < this.dependencyField.length; index++) {
              dependentFieldValue[this._registeredDependencies[index]] =
                this.notifyContext.has(this.dependencyField[index])
                  ? this.notifyContext.get(this.dependencyField[index])
                  : null;
            }
            this.doChangeNotify(response.subject, dependentFieldValue);
            if (
              this._dropdownMode === 'BASIC' &&
              this.dependencyField.length > 0
            ) {
              this._refereshDataBasic(dependentFieldValue);
            } else if (
              this._dropdownMode === 'SEARCHABLE' &&
              this.dependencyField.length > 0
            ) {
              this._refereshDataSearchable(dependentFieldValue);
              //console.log('subscribing to value changes');
            }
          }
        });
    }

    this.controlStatus$ = this.formControl.statusChanges.subscribe(
      (res: FormControlStatus) => {
        //console.log('status changed');
        if (res === 'INVALID') {
          this.errorCode = this.getErrorCode();
          console.log('error identified', this.formControlName, this.errorCode);
          this.changeDetectorRef.markForCheck();
        } else {
          this.errorCode = '';
        }
        if (res === 'PENDING') {
          this.loading = true;
        } else {
          this.loading = false;
        }
      }
    );
    if (this._dropdownMode === 'BASIC' && this.dependencyField.length === 0) {
      this._refereshDataBasic(null);
    } else if (this._dropdownMode === 'SEARCHABLE' && this.dependencyField.length === 0) {
      this._refereshDataSearchable(null);
    }

    if (this._dropdownMode === 'SEARCHABLE') {
      console.log('subscribing to value changes');
      this.formControl.valueChanges.subscribe((value: any) => {
        console.log('Change in value', value, typeof (value));
        if (typeof (value) === 'string' && value !== '') {
          this.setSelectableData(this._dataService.lookup({ 'searchText': value }));
        }
        else {
          this.onChange(value);
        }
      }
      )
    }

    this.doPostInit();
  }


  private _refereshDataBasic(key: { [k: string]: any } | null) {
    try {
      if (this._dataService && this._dataService.lookup) {
        let keys: { [k: string]: any } = {};
        if (key) {
          for (let i = 0; i < this._registeredDependencies.length; i++) {
            keys[this._registeredDependencies[i]] =
              key[this._registeredDependencies[i]];
          }
        }
        this.setSelectableData(this._dataService.lookup(keys));
      }
    } catch (error) {
      console.error('Data Service not implemented');
    }
  }

  private _refereshDataSearchable(key: { [k: string]: any } | null) {
    try {
      if (this._dataService) {
        this.setSelectableData(this._loadDynamicDropdown());
      }
    } catch (error) {
      console.error('Data Service not implemented');
    }
  }

  private _loadDynamicDropdown() {
    return () => {
      return this.inputChange$.pipe(
        distinctUntilChanged(),
        startWith(''),
        debounceTime(200),
        switchMap((value) => {
          this.loading = true;
          return this._dataService
            .lookup({ searchText: value })()
            .pipe(
              finalize(() => {
                this.loading = false;
                /** loading spinner need to handle here */
              })
            );
        })
      );
    };
    /*this.inputChange$.pipe(
      distinctUntilChanged(),
      startWith(''),
      debounceTime(200),
      switchMap((value) => {
        this.loading = true;
        return this._dataService
          .lookup(criteria)()
          .pipe(
            finalize(() => {
              this.loading = false;
            })
          );
      })
    );*/
  }

  /** @description destroy lifecycle hook */
  ngOnDestroy(): void {
    if (this.changePublisher$) {
      this.changePublisher$.unsubscribe();
    }
    if (this.controlStatus$) {
      this.controlStatus$.unsubscribe();
    }
    this.notifyContext.clear();
    this.doDestroy();
  }

  /** @description return the error code */
  private getErrorCode() {
    return Object.keys(this.formControl.errors as ValidationErrors)[0];
  }

  /** @description control vaule accessor */
  onChange: any = () => { };

  onTouched: () => void = () => { };

  setDisabledState?(isDisabled: boolean): void {
    console.error('isdisable', this.formControlName, isDisabled);
  }

  registerOnChange(fn: any): void {
    console.log(this.formControlName, 'registerchange');
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
    if (this.formControl && obj != this.formControl.value) {
      this.formControl.setValue(obj, { emitEvent: false });
    }
  }

  public refreshState(): void {
    this.doRefresh();
  }

  public onInput(value: any) {
    this.input$.next(value);
  }

  protected onItemSelect(value: { id: string; text: string }): void {
    this.onChange(value);
    this.onTouched();
  }

  protected itemDisplay(value: { id: string; text: string }): string {
    return value && value.text ? value.text : '';
  }

  /** @description override methods to access the angular life cycle hooks */
  protected doPreInit(): void { }
  protected doPostInit(): void { }
  protected doChangeNotify(subject: string, payload: any): void { }
  protected doDestroy(): void { }
  protected doRefresh(): void { }
}
